import type { AdminAuditActorFacetValue, AdminAuditLogFacetField, AdminAuditLogFacetFilters } from '@/api/models';
import { payApiClient } from '@/api/client';
import { useApiResource } from '@/hooks/useApiResource';

export interface AuditFacetState {
  values: string[];
  /**
   * Present only for the `actor` field: the distinct actors paired with their
   * last-known display names, so the combobox can label ids with names. When
   * the API sends plain `values` (older responses, other fields) this stays
   * `undefined` and the caller renders the bare values.
   */
  actors?: AdminAuditActorFacetValue[];
  loading: boolean;
  /** The facet source is unusable (request failed or malformed) — the caller degrades to a plain text input, never blocks filtering. */
  failed: boolean;
}

/**
 * Loads the distinct values of one audit-log column for a filter combobox,
 * refetching whenever the narrowing filters change (e.g. the actor facet
 * follows the selected actor types). A failure is reported as `failed`
 * rather than thrown: the facet population is a convenience layer over the
 * filter, so the worst outcome of losing it must be typing the value by
 * hand — never an unusable filter form.
 */
export function useAuditFacet(
  field: AdminAuditLogFacetField,
  filters: AdminAuditLogFacetFilters,
  enabled = true,
): AuditFacetState {
  const { data, loading, error } = useApiResource(
    () => (enabled ? payApiClient.getAuditLogFacets(field, filters) : Promise.resolve({ values: [] })),
    JSON.stringify([field, filters, enabled]),
  );

  const actors = data && Array.isArray(data.actors) ? data.actors : undefined;
  const plainValues = data && Array.isArray(data.values) ? data.values : undefined;
  const malformed = data !== undefined && actors === undefined && plainValues === undefined;
  return {
    values: actors ? actors.map((pair) => pair.actor) : (plainValues ?? []),
    actors,
    loading: enabled && loading,
    failed: Boolean(error) || malformed,
  };
}
